"use client";
import React from "react";
import Link from "next/link";
import { useIntl } from "react-intl";

import { IJob } from "@globalTypes";
import Collapsable, { CollapsableLoading } from "./collapsable";

import { Skeleton } from "@components/ui/skeleton";
import CompanyLogo from "./company-logo";

interface SimilarJobsProps {
  data: IJob;
  jobs: any[];
}

const SimilarJobs = ({ data, jobs }: SimilarJobsProps) => {
  const intl = useIntl();
  const translation: any = intl.messages["job-details"];

  return (
    <Collapsable title={translation["similar-jobs"]} id="similar-jobs">
      <ul className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        {jobs?.map((job) => (
          <Job key={job.id} job={job} />
        ))}
      </ul>
    </Collapsable>
  );
};

export default SimilarJobs;

interface JobProps {
  job: any;
}

const Job = ({ job }: JobProps) => {
  return (
    <li className="flex flex-col">
      <div className="h-10"></div>
      <Link
        href={`/jobs/view/${job?.slug}`}
        className="flex flex-col border rounded-lg text-center p-4 h-full hover:opacity-70 transition-opacity"
      >
        <CompanyLogo
          employer={job?.employer}
          className="-mt-14 mb-3 w-20 h-20 sm:w-20 sm:h-20 text-3xl sm:text-4xl"
        />
        <h4 className="text-neutral-700 text-sm mb-2">
          {job?.employer?.employer_profile?.companyName ||
            job?.employer?.firstName}{" "}
          - {job?.country?.country}
        </h4>
        <h3 className="text-base font-semibold mb-6">{job?.title}</h3>

        <ul className="flex gap-3 justify-center flex-wrap mt-auto">
          {job?.jobType?.jobType ? (
            <li className="py-1 px-3 rounded-full bg-primary/20 text-primary text-xs font-medium text-neutral-700">
              {job?.jobType?.jobType}
            </li>
          ) : null}
          {job?.jobShift?.jobShift ? (
            <li className="py-1 px-3 rounded-full bg-gray-100 text-xs font-medium text-neutral-700">
              {job?.jobShift?.jobShift}
            </li>
          ) : null}
        </ul>
      </Link>
    </li>
  );
};

export const JobListingLoading = () => {
  const jobs = [...Array(6)].fill(null);

  return (
    <CollapsableLoading>
      <ul className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        {jobs?.map((job, index) => (
          <li key={index} className="flex flex-col">
            <div className="h-10"></div>
            <div className="flex flex-col border rounded-lg text-center p-4 h-full hover:opacity-70 transition-opacit">
              <Skeleton className="w-20 h-20 mx-auto -mt-14 mb-3 rounded-md" />
              <Skeleton className="h-5 w-28 rounded-full mx-auto mb-2" />
              <Skeleton className="h-6 w-full rounded-full mx-auto mb-6" />
              <div className="flex gap-3 justify-center flex-wrap mt-auto">
                <Skeleton className="h-6 w-14 rounded-full" />
                <Skeleton className="h-6 w-14 rounded-full" />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </CollapsableLoading>
  );
};
